import { type FC, useState, useCallback, useRef, useEffect } from "react";
import type { AgentMessageFragmentClarifyingQuestions } from "@/hooks/use-stream-conversation";
import { Button } from "@/components/flexkit/Button";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { cn } from "@/components/podkit/lib/cn";
import { IconChevronTopSmall } from "central-icons/IconChevronTopSmall";
import { IconChevronDownSmall } from "central-icons/IconChevronDownSmall";
import { Counter } from "@/components/flexkit/badge/Counter";
import type { Answer } from "@/components/ai/clarifying-questions-utils";
import { isTouchDevice } from "@/utils/is-touch-device";

/** Converts a 0-based index to a letter: 0→"A", 1→"B", 2→"C", etc. */
function choiceLetter(index: number): string {
    return String.fromCharCode(65 + index);
}

type FragmentClarifyingQuestionsProps = {
    fragment: AgentMessageFragmentClarifyingQuestions;
    onSubmit: (answers: Map<string, Answer>) => void;
};

export const FragmentClarifyingQuestions: FC<FragmentClarifyingQuestionsProps> = ({ fragment, onSubmit }) => {
    const { questions } = fragment;
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState<Map<string, Answer>>(new Map());
    const [otherText, setOtherText] = useState<Map<string, string>>(new Map());
    const [focusedChoiceIndex, setFocusedChoiceIndex] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [contentVisible, setContentVisible] = useState(true);
    const prevIndexRef = useRef(currentIndex);
    const otherInputRef = useRef<HTMLInputElement>(null);
    const arrowNavRef = useRef(false);
    const otherTextRef = useRef(otherText);
    otherTextRef.current = otherText;
    const focusedChoiceIndexRef = useRef(focusedChoiceIndex);
    focusedChoiceIndexRef.current = focusedChoiceIndex;

    const currentQuestion = questions[currentIndex];
    const totalQuestions = questions.length;
    const isLastQuestion = currentIndex === totalQuestions - 1;

    const currentAnswer = answers.get(currentQuestion?.questionId);
    const currentOtherText = otherText.get(currentQuestion?.questionId) || "";
    const isOtherSelected = currentAnswer?.type === "other";
    const hasValidAnswer = currentAnswer && (currentAnswer.type !== "other" || currentAnswer.value?.trim());

    const handleSelectChoice = useCallback((questionId: string, choiceText: string) => {
        setAnswers((prev) => {
            const next = new Map(prev);
            next.set(questionId, { type: "choice", value: choiceText });
            return next;
        });
    }, []);

    const handleSelectOther = useCallback((questionId: string) => {
        setAnswers((prev) => {
            const next = new Map(prev);
            next.set(questionId, { type: "other", value: otherTextRef.current.get(questionId) || "" });
            return next;
        });
    }, []);

    const handleOtherTextChange = useCallback((questionId: string, text: string) => {
        setOtherText((prev) => {
            const next = new Map(prev);
            next.set(questionId, text);
            return next;
        });
        setAnswers((prev) => {
            const next = new Map(prev);
            next.set(questionId, { type: "other", value: text });
            return next;
        });
    }, []);

    const handleSkip = useCallback(() => {
        const questionId = currentQuestion?.questionId;
        const updatedAnswers = new Map(answers);
        if (questionId) {
            updatedAnswers.set(questionId, { type: "skipped" });
            setAnswers(updatedAnswers);
        }
        if (isLastQuestion) {
            onSubmit(updatedAnswers);
        } else {
            setCurrentIndex((prev) => prev + 1);
        }
    }, [currentQuestion?.questionId, isLastQuestion, answers, onSubmit]);

    const handleContinue = useCallback(() => {
        if (isLastQuestion) {
            onSubmit(answers);
        } else {
            setCurrentIndex((prev) => prev + 1);
        }
    }, [isLastQuestion, answers, onSubmit]);

    const handleOtherInputKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                e.stopPropagation();
                if (hasValidAnswer) {
                    handleContinue();
                }
                return;
            }

            // Let arrow keys and Escape bubble to the container's handleKeyDown
            if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "Escape") {
                return;
            }

            // Prevent letter keys from bubbling to the container's handleKeyDown
            e.stopPropagation();
        },
        [handleContinue, hasValidAnswer],
    );

    const handleNavigate = useCallback(
        (direction: "up" | "down") => {
            if (direction === "up" && currentIndex > 0) {
                setCurrentIndex((prev) => prev - 1);
            } else if (direction === "down" && currentIndex < totalQuestions - 1) {
                setCurrentIndex((prev) => prev + 1);
            }
        },
        [currentIndex, totalQuestions],
    );

    const getTotalChoices = useCallback(() => {
        if (!currentQuestion) return 0;
        return currentQuestion.choices.length + (currentQuestion.allowOther ? 1 : 0);
    }, [currentQuestion]);

    const selectByIndex = useCallback(
        (index: number) => {
            if (!currentQuestion) return;
            const isOtherIndex = index === currentQuestion.choices.length;
            if (isOtherIndex && currentQuestion.allowOther) {
                handleSelectOther(currentQuestion.questionId);
            } else if (index < currentQuestion.choices.length) {
                const choice = currentQuestion.choices[index];
                handleSelectChoice(currentQuestion.questionId, choice.text);
            }
        },
        [currentQuestion, handleSelectChoice, handleSelectOther],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            const totalChoices = getTotalChoices();
            if (totalChoices === 0) return;

            // Let focused child buttons (chevrons, skip) handle their own Enter/Space natively
            if (e.target !== containerRef.current && (e.key === "Enter" || e.key === " ")) {
                return;
            }

            // Letter keys select the corresponding answer (a=0, b=1, etc.)
            const letterIndex = e.key.toLowerCase().charCodeAt(0) - 97; // 'a' = 97
            if (letterIndex >= 0 && letterIndex < totalChoices && e.key.length === 1 && /^[a-z]$/i.test(e.key)) {
                e.preventDefault();
                arrowNavRef.current = false;
                setFocusedChoiceIndex(letterIndex);
                selectByIndex(letterIndex);
                return;
            }

            const currentFocused = focusedChoiceIndexRef.current;
            switch (e.key) {
                case "ArrowDown": {
                    e.preventDefault();
                    arrowNavRef.current = true;
                    const nextDown = currentFocused === null ? 0 : (currentFocused + 1) % totalChoices;
                    setFocusedChoiceIndex(nextDown);
                    selectByIndex(nextDown);
                    containerRef.current?.focus();
                    break;
                }
                case "ArrowUp": {
                    e.preventDefault();
                    arrowNavRef.current = true;
                    const nextUp =
                        currentFocused === null ? totalChoices - 1 : (currentFocused - 1 + totalChoices) % totalChoices;
                    setFocusedChoiceIndex(nextUp);
                    selectByIndex(nextUp);
                    containerRef.current?.focus();
                    break;
                }
                case "Enter":
                    e.preventDefault();
                    if (hasValidAnswer) {
                        handleContinue();
                    }
                    break;
                case "Escape":
                    e.preventDefault();
                    handleSkip();
                    break;
            }
        },
        [getTotalChoices, hasValidAnswer, handleContinue, handleSkip, selectByIndex],
    );

    useEffect(() => {
        setFocusedChoiceIndex(null);
        containerRef.current?.focus();
    }, [currentIndex]);

    // Auto-focus the "Other" text input when selected via click or letter key,
    // but not during arrow navigation (container keeps focus for continued arrows)
    useEffect(() => {
        if (isOtherSelected && !arrowNavRef.current) {
            requestAnimationFrame(() => otherInputRef.current?.focus());
        }
    }, [isOtherSelected]);

    // Crossfade when navigating between questions
    useEffect(() => {
        if (prevIndexRef.current !== currentIndex) {
            prevIndexRef.current = currentIndex;
            setContentVisible(false);
            const timer = setTimeout(() => {
                setContentVisible(true);
            }, 50);
            return () => clearTimeout(timer);
        }
    }, [currentIndex]);

    // setTimeout ensures focus wins over ChatInput's autoFocus
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            containerRef.current?.focus();
        }, 0);
        return () => clearTimeout(timeoutId);
    }, []);

    if (!currentQuestion) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            className="overflow-hidden rounded-t-lg border border-border-base bg-surface-primary shadow-sm outline-none focus:outline-none focus:ring-0"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            data-testid="clarifying-questions-panel"
        >
            <div className="flex items-center justify-between px-4 pb-1.5 pt-3">
                <Counter
                    variant="default"
                    counter={`Question ${currentIndex + 1}`}
                    className="bg-surface-muted px-2.5 py-1 text-base font-normal"
                />
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => handleNavigate("up")}
                        disabled={currentIndex === 0}
                        className="flex items-center justify-center text-content-secondary disabled:opacity-30"
                        aria-label="Previous question"
                        data-tracking-id="clarifying-questions-previous"
                    >
                        <IconChevronTopSmall size={20} />
                    </button>
                    <span className="text-sm text-content-tertiary">
                        {currentIndex + 1}/{totalQuestions}
                    </span>
                    <button
                        type="button"
                        onClick={() => handleNavigate("down")}
                        disabled={currentIndex === totalQuestions - 1}
                        className="flex items-center justify-center text-content-secondary disabled:opacity-30"
                        aria-label="Next question"
                        data-tracking-id="clarifying-questions-next"
                    >
                        <IconChevronDownSmall size={20} />
                    </button>
                </div>
            </div>

            <div
                className={cn(
                    "px-4 pb-3 transition-opacity duration-200 ease-in-out",
                    contentVisible ? "opacity-100" : "opacity-0",
                )}
                aria-live="polite"
                aria-atomic="true"
            >
                <div className="mb-2">
                    <span className="select-text text-base font-normal text-content-primary">
                        {currentQuestion.question}
                    </span>
                </div>

                <div className="space-y-1">
                    {currentQuestion.choices.map((choice, index) => {
                        const isSelected = currentAnswer?.type === "choice" && currentAnswer.value === choice.text;
                        const isFocused = focusedChoiceIndex === index;
                        return (
                            <button
                                key={choice.label}
                                type="button"
                                tabIndex={-1}
                                onClick={() => {
                                    arrowNavRef.current = false;
                                    setFocusedChoiceIndex(null);
                                    handleSelectChoice(currentQuestion.questionId, choice.text);
                                    containerRef.current?.focus();
                                }}
                                className={cn(
                                    "flex w-full min-w-0 items-center gap-2 rounded-md p-1 text-left outline-none transition-colors",
                                    isSelected
                                        ? "bg-surface-brand-subtle text-content-primary"
                                        : isFocused
                                          ? "bg-surface-secondary ring-1 ring-border-brand"
                                          : "hover:bg-surface-secondary",
                                )}
                                data-tracking-id="clarifying-questions-choice"
                            >
                                <span
                                    className={cn(
                                        "flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-md font-mono text-base font-medium",
                                        isSelected
                                            ? "bg-content-brand text-content-primary-inverted"
                                            : "bg-surface-brand-subtle text-content-brand",
                                    )}
                                >
                                    {choiceLetter(index)}
                                </span>
                                <span className="min-w-0 flex-1 whitespace-normal break-words text-base font-normal text-content-primary">
                                    {choice.text}
                                </span>
                            </button>
                        );
                    })}

                    {currentQuestion.allowOther &&
                        (() => {
                            const otherIndex = currentQuestion.choices.length;
                            const isOtherFocused = focusedChoiceIndex === otherIndex;
                            return (
                                <button
                                    type="button"
                                    tabIndex={-1}
                                    onClick={() => {
                                        if (!isOtherSelected) {
                                            arrowNavRef.current = false;
                                            setFocusedChoiceIndex(null);
                                            handleSelectOther(currentQuestion.questionId);
                                        }
                                    }}
                                    className={cn(
                                        "flex w-full min-w-0 items-center gap-2 rounded-md p-1 text-left outline-none transition-colors",
                                        isOtherSelected
                                            ? "bg-surface-brand-subtle text-content-primary"
                                            : isOtherFocused
                                              ? "cursor-pointer bg-surface-brand-subtle ring-1 ring-border-brand"
                                              : "cursor-pointer hover:bg-surface-secondary",
                                    )}
                                    data-tracking-id="clarifying-questions-other"
                                >
                                    <span
                                        className={cn(
                                            "flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-md font-mono text-base font-medium",
                                            isOtherSelected
                                                ? "bg-content-brand text-content-primary-inverted"
                                                : "bg-surface-brand-subtle text-content-brand",
                                        )}
                                    >
                                        {choiceLetter(currentQuestion.choices.length)}
                                    </span>
                                    {isOtherSelected ? (
                                        <input
                                            ref={otherInputRef}
                                            type="text"
                                            placeholder="Type your answer..."
                                            value={currentOtherText}
                                            onChange={(e) =>
                                                handleOtherTextChange(currentQuestion.questionId, e.target.value)
                                            }
                                            className="min-w-0 flex-1 border-none bg-transparent text-sm text-content-primary outline-none placeholder:text-content-muted focus:ring-0"
                                            autoFocus
                                            onKeyDown={handleOtherInputKeyDown}
                                        />
                                    ) : (
                                        <span className="text-base text-content-tertiary">Other...</span>
                                    )}
                                </button>
                            );
                        })()}
                </div>
            </div>

            <div className="flex items-center justify-end gap-2 px-4 pb-5 pt-0">
                <button
                    type="button"
                    onClick={handleSkip}
                    className="text-sm font-medium text-content-secondary hover:text-content-primary"
                    data-tracking-id="clarifying-questions-skip"
                >
                    Skip
                </button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={handleContinue}
                    disabled={!hasValidAnswer}
                    data-tracking-id="clarifying-questions-continue"
                    data-testid="clarifying-questions-action"
                >
                    <span>{isLastQuestion ? "Submit" : "Next"}</span>
                    {!isTouchDevice() && (
                        <span className="ml-1 opacity-60">
                            <KeyboardCombo
                                keys={["Enter"]}
                                className="bg-content-primary-inverted/20 text-content-primary-inverted"
                            />
                        </span>
                    )}
                </Button>
            </div>
        </div>
    );
};
